import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../common/reducers/utilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = template => template.id;

const templateSlice = createSlice({
    name: 'templates',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(template => {
                state.data[key(template)] = template;
            })
        },
        setActive: (state, action) => {
            Object.keys(state.data).forEach(id => {
                if (state.data[id].inactive) {
                    delete state.data[id];
                }
            });
            action.payload.forEach(template => state.data[key(template)] = template);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(template => {
                state.data[key(template)] = template;
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(template => delete state.data[key(template)]);
        },
    },
    extraReducers: builder => {
        builder.addCase(logout, () => templateSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'template_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'template_one');

const fetchTemplate = aggregatingFetch(
    id => network => network.get('/api/v1/template/' + encodeURIComponent(id), data => templateSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.get('/api/v1/template', data => templateSlice.actions.set({ data, ids: [ids] }), visualizeFailure('get')),
    { primitiveKey: true }
);
const fetchActiveTemplates = network => network.post('/api/v1/templateSearch', { inactive: false }, templateSlice.actions.setActive, visualizeFailure('get'));
export const createTemplate = (network, template) => network.post('/api/v1/template', template, templateSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateTemplate = (network, template) => network.put('/api/v1/template', template, templateSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeTemplate = (network, template) => network.delete('/api/v1/template/' + encodeURIComponent(template.id), templateSlice.addOrUpdate, visualizeFailure('remove'));
export const instnatiate = (network, id, start, eventCount, repsPerEvent, carryForward, ownerCarryForward, owners) => network.post('/api/v1/instantiateTemplate', {
    id, start, event_count: eventCount, reps_per_event: repsPerEvent, carry_forward: carryForward, owner_carry_forward: ownerCarryForward, owners
}, visualizeFailure('create'), visualizeFailure('create'));
// #endregion

// #region selectors
const selectTemplateData = state => state.templates.data;
const selectTemplatesAsArray = createSelector([selectTemplateData], templates => Object.values(templates));
const selectTemplate = id => createSelector([selectTemplateData], templates => templates[id]);
const selectTemplates = ids => createSelector([selectTemplateData], templates => ids.reduce((res, id) => {
    if (templates[id]) {
        res.push(templates[id]);
    }
    return res;
}, []));
const selectActiveTemplates = createSelector([selectTemplatesAsArray], templates => templates.filter(t => !t.inactive));
// #endregion

const listen = {
    templateCreated: (template, { dispatch, websocket }) => {
        markFetched(websocket, 'template', key(template))
        dispatch(templateSlice.actions.addOrUpdate(template))
    },
    templateUpdated: (template, { dispatch }) => dispatch(templateSlice.actions.addOrUpdate(template)),
}
const fullRoles = ['administrator', 'template manager'];
const readRoles = ['auditor', 'template auditor'];
const checkPermissions = (credentials, template, requiredRoles) => credentials.hasRole(requiredRoles, { office: template?.event?.office, client: template?.event?.client, location: template?.event?.locationid });
export const canCreateTemplate = (credentials, template) => checkPermissions(credentials, template, fullRoles);
export const canUpdateTemplate = canCreateTemplate;
export const canRemoveTemplate = canCreateTemplate;
export const canViewTemplate = (credentials, template) => checkPermissions(credentials, template, fullRoles.concat(readRoles));
const canQueryTemplate = (credentials) => credentials.hasRole(fullRoles.concat(readRoles));

const templateInitByID = initMapFactory(fetchTemplate, listen);
registerEntityBasedFetch('template', templateInitByID);

const cleanup = { root: templateSlice.name, all: selectTemplatesAsArray, removeAll: templateSlice.actions.remove, key };
const options = { cleanup, condition: canQueryTemplate }
export const [useTemplate] = dataHookWithCredentialsFactory(selectTemplate, templateInitByID, options);
export const [useTemplates] = dataHookWithCredentialsFactory(selectTemplates, initMultiMapFactory(templateInitByID, listen), options);
export const [useActiveTemplates] = dataHookFactory(selectActiveTemplates, queryMapFactory(fetchActiveTemplates, 'id', templateInitByID, listen, { allowEmpty: true })(), { cleanup });

const reducer = { name: templateSlice.name, reducer: templateSlice.reducer }
export default reducer;